import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import InvoiceLineItemTable from '../../../components/tables/InvoiceLineItemTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useInvoiceLineItemApi } from '../../../hooks/useInvoiceLineItemApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { InvoiceLineItemDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const InvoiceLineItemList: React.FC = () => {
  const { search, remove, extract, isLoading, error } = useInvoiceLineItemApi();
  const [items, setItems] = useState<InvoiceLineItemDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Invoice Line Items', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Invoice Line Items:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: InvoiceLineItemDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting InvoiceLineItem:', error);
    }
  };

  // Add export handler
  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Invoice Line Items:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/invoicelineitems"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.InvoiceLineItem.View"
    >
      <Card
        title="Manage Invoice Line Items"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button type="button" variant="secondary" onClick={handleExport}>
              Export
            </Button>
            <AddNewButton
              onClick={() => navigate('/admin/invoicelineitems/create')}
            />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="invoiceId"
              label="Search by Invoice Id"
              value={searchFilters.invoiceId || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  invoiceId: e.target.value,
                })
              }
              placeholder="Search by Invoice Id"
            />
            <FormField
              id="description"
              label="Search by Description"
              value={searchFilters.description || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  description: e.target.value,
                })
              }
              placeholder="Search by Description"
            />
            <FormField
              id="priceExcl"
              label="Search by Price Excl"
              value={searchFilters.priceExcl || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  priceExcl: e.target.value,
                })
              }
              placeholder="Search by Price Excl"
            />
            <FormField
              id="priceTax"
              label="Search by Price Tax"
              value={searchFilters.priceTax || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, priceTax: e.target.value })
              }
              placeholder="Search by Price Tax"
            />
            <FormField
              id="priceIncl"
              label="Search by Price Incl"
              value={searchFilters.priceIncl || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  priceIncl: e.target.value,
                })
              }
              placeholder="Search by Price Incl"
            />
            <FormField
              id="quantity"
              label="Search by Quantity"
              value={searchFilters.quantity || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, quantity: e.target.value })
              }
              placeholder="Search by Quantity"
            />
            <FormField
              id="lineExcl"
              label="Search by Line Excl"
              value={searchFilters.lineExcl || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, lineExcl: e.target.value })
              }
              placeholder="Search by Line Excl"
            />
            <FormField
              id="lineTax"
              label="Search by Line Tax"
              value={searchFilters.lineTax || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, lineTax: e.target.value })
              }
              placeholder="Search by Line Tax"
            />
            <FormField
              id="lineDiscount"
              label="Search by Line Discount"
              value={searchFilters.lineDiscount || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  lineDiscount: e.target.value,
                })
              }
              placeholder="Search by Line Discount"
            />
            <FormField
              id="lineIncl"
              label="Search by Line Incl"
              value={searchFilters.lineIncl || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, lineIncl: e.target.value })
              }
              placeholder="Search by Line Incl"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <InvoiceLineItemTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/invoicelineitems/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default InvoiceLineItemList;
